import { CloseOutlined } from '@ant-design/icons';
import { Card, Col, Flex, Row, Typography } from 'antd';
import { useTranslation } from 'translations/hooks';

import { StatusTag } from 'business/quality-control/pages/segment-history/components/status-tag';
import { SegmentHistory } from 'business/quality-control/pages/segment-history/hooks/data/types';
import { Quality_SegmentHistoryStatusEnum_Enum } from 'generated/graphql';
import { formatDate } from 'technical/format/date';
import Button from 'ui/button';
import { CapSidebar } from 'ui/sidebar';

import styles from './index.module.scss';

interface SegmentDrawerProps {
  segmentId: string | null;
  segments: SegmentHistory[];
  open: boolean;
  onClose: () => void;
  openDetailsDrawer: (segment: SegmentHistory) => void;
}

export const SegmentMobileDrawer = ({
  segmentId,
  segments,
  open,
  onClose,
  openDetailsDrawer,
}: SegmentDrawerProps) => {
  const { t } = useTranslation();

  const segment = segments.find((s) => s.id === segmentId);

  const shouldDisplayDetails = segment?.history.some(
    (history) =>
      history.status === Quality_SegmentHistoryStatusEnum_Enum.Quarantine ||
      (history.status === Quality_SegmentHistoryStatusEnum_Enum.Rejected &&
        history.segmentRefusal != null),
  );

  if (!segment) {
    return null;
  }

  const switchToDetailsDrawer = () => {
    onClose();
    openDetailsDrawer(segment);
  };

  return (
    <CapSidebar
      title={t('qualityControl.segmentHistory.mobileDrawer.title')}
      onClose={onClose}
      open={open}
      closable={false}
      forceRender
      placement="bottom"
      height={425}
      extra={<Button type="text" icon={<CloseOutlined />} onClick={onClose} />}
    >
      <Flex gap={24} vertical>
        <Card className={styles.card} variant="borderless">
          <Row align="middle" className={styles.rows}>
            <Col span={12}>
              <Typography.Text className={styles.label}>
                {t(
                  'qualityControl.segmentHistory.drawer.segmentInfos.serialNumber',
                )}
              </Typography.Text>
              <Typography.Text className={styles.value}>
                {segment.serialNumber}
              </Typography.Text>
            </Col>
            <Col span={12}>
              <StatusTag status={segment.status ?? ''} />
            </Col>
          </Row>

          <Row gutter={[16, 24]} className={styles.rows}>
            <Col span={8}>
              <Typography.Text className={styles.label}>
                {t(
                  'qualityControl.segmentHistory.drawer.segmentInfos.segmentType',
                )}
              </Typography.Text>
              <Typography.Text className={styles.value}>
                {segment.segmentType ?? '-'}
              </Typography.Text>
            </Col>
            <Col span={6}>
              <Typography.Text className={styles.label}>
                {t(
                  'qualityControl.segmentHistory.mobileDrawer.segmentInfos.mold',
                )}
              </Typography.Text>
              <Typography.Text className={styles.value}>
                {segment.mold ?? '-'}
              </Typography.Text>
            </Col>
          </Row>

          <Row gutter={[16, 24]} className={styles.rows}>
            <Col span={8}>
              <Typography.Text className={styles.label}>
                {t(
                  'qualityControl.segmentHistory.mobileDrawer.segmentInfos.manufactureDate',
                )}
              </Typography.Text>
              <Typography.Text className={styles.value}>
                {formatDate(segment.manufactureDate)}
              </Typography.Text>
            </Col>
            <Col span={10}>
              <Typography.Text className={styles.label}>
                {t(
                  'qualityControl.segmentHistory.mobileDrawer.segmentInfos.site',
                )}
              </Typography.Text>
              <Typography.Text className={styles.value}>
                {segment.lastScanSite ?? '-'}
              </Typography.Text>
            </Col>
            <Col span={6}>
              <Typography.Text className={styles.label}>
                {t(
                  'qualityControl.segmentHistory.drawer.segmentInfos.ringNumber',
                )}
              </Typography.Text>
              <Typography.Text className={styles.value}>
                {segment.ringNumber ?? '-'}
              </Typography.Text>
            </Col>
          </Row>
        </Card>
        {shouldDisplayDetails ? (
          <Button onClick={switchToDetailsDrawer}>
            {t('qualityControl.segmentHistory.mobileDrawer.expandDetails')}
          </Button>
        ) : null}
      </Flex>
    </CapSidebar>
  );
};
